'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, LogIn, Plus, UserPlus, X } from 'lucide-react';
import type {
  FinderGroup,
  HouseholdMember,
  InquiryPerson,
  MyInquiry,
  OtherPerson,
} from '@/lib/types';
import { InquiryError, fetchHousehold, submitInquiry } from '@/lib/api';
import { getMpWidgetToken, onMpTokenChange, signInAndReturn } from '@/lib/campus';
import { Avatar } from './Avatar';

interface InquiryFormProps {
  apiBaseUrl: string;
  group: FinderGroup;
  finderUrl: string;
  inquiryFormUrl: string;
  /** 'dark' on the See Jesus pane: cream text, gold labels, foil buttons */
  tone?: 'light' | 'dark';
  /** Household members who already inquired into this group are shown, not selectable */
  inquired?: MyInquiry[];
  /** Inquiries went out for these people */
  onSent?: (names: string[]) => void;
  /** Opens the "help me find a group" form; without it the link is used */
  onHelp?: () => void;
}

const TONES = {
  light: {
    muted: 'text-gray-600',
    faint: 'text-gray-500',
    label: 'text-gray-500',
    strong: 'text-[#1c2b39]',
    primary: 'bg-[#299c8f] text-white',
    secondary: 'bg-[#1c2b39] text-white',
    selected: 'bg-[#1c2b39] text-white shadow-md',
    check: 'bg-[#299c8f] text-white',
    tick: 'bg-[#299c8f] text-white ring-[#1c2b39]',
    skeleton: 'bg-gray-100',
    link: 'text-[#299c8f]',
    ghost: 'text-[#1c2b39] hover:bg-gray-100',
  },
  dark: {
    muted: 'text-[#f6efe9]/75',
    faint: 'text-[#f6efe9]/60',
    label: 'text-[#c79854]',
    strong: 'text-[#f6efe9]',
    primary: 'sj-foil sj-foil-btn text-black',
    secondary: 'bg-white/10 text-[#f6efe9] ring-1 ring-white/20',
    selected: 'sj-foil text-black shadow-md',
    check: 'sj-foil text-black',
    tick: 'bg-black text-[#e8c998] ring-[#c79854]',
    skeleton: 'bg-white/10',
    link: 'text-[#e8c998]',
    ghost: 'text-[#f6efe9] hover:bg-white/10',
  },
} as const;

const FIELD =
  'w-full rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none';

const CHIP =
  'inline-flex items-center gap-2 rounded-full py-1.5 pr-4 pl-1.5 text-sm font-semibold transition-all';
const CHIP_IDLE =
  'bg-white text-[#1c2b39] ring-1 ring-gray-200 hover:scale-105 hover:shadow-md active:scale-95';
const CHIP_DASHED =
  'bg-white text-[#1c2b39] ring-1 ring-gray-300 ring-dashed hover:scale-105 hover:shadow-md active:scale-95';

const EMPTY_OTHER: OtherPerson = { firstName: '', lastName: '', email: '', phone: '' };
const MAX_PEOPLE = 10;

const trimOther = (o: OtherPerson): OtherPerson => ({
  firstName: o.firstName.trim(),
  lastName: o.lastName.trim(),
  email: o.email.trim(),
  phone: o.phone.trim(),
});
const otherValid = (o: OtherPerson) =>
  Boolean(o.firstName.trim() && o.lastName.trim() && (o.email.trim() || o.phone.trim()));

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Where a signed-out visitor's draft waits while they sign in. Session
 * storage survives MP's hosted sign-in round trip (same tab) and is gone
 * when the tab closes. `pendingSend` means they pressed Send: once the
 * token lands the inquiry goes out, no second click.
 */
interface Draft {
  comments: string;
  selected: number[];
  others: OtherPerson[];
  /** The "someone else" fields as they were, if they were open. */
  pending: OtherPerson | null;
  pendingSend: boolean;
  at: number;
}
const DRAFT_MAX_AGE_MS = 30 * 60 * 1000;
const draftKey = (groupId: number) => `group-finder:inquiry-draft:${groupId}`;

function readDraft(groupId: number): Draft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(groupId));
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<Draft>;
    if (!d.at || Date.now() - d.at > DRAFT_MAX_AGE_MS) {
      sessionStorage.removeItem(draftKey(groupId));
      return null;
    }
    return {
      comments: d.comments ?? '',
      selected: Array.isArray(d.selected) ? d.selected : [],
      others: Array.isArray(d.others) ? d.others : [],
      pending: d.pending ?? null,
      pendingSend: Boolean(d.pendingSend),
      at: d.at,
    };
  } catch {
    return null;
  }
}
function writeDraft(groupId: number, d: Draft) {
  try {
    sessionStorage.setItem(draftKey(groupId), JSON.stringify(d));
  } catch {
    /* storage blocked: they will retype */
  }
}
function clearDraft(groupId: number) {
  try {
    sessionStorage.removeItem(draftKey(groupId));
  } catch {
    /* nothing to clear */
  }
}

const CTA =
  'inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';

/**
 * "We'd like to join." Pick anyone in the household (several at once), add
 * people who are not in MP, one message, one Send: the API writes a row
 * per person. Signed-in only: MP's leader notification joins the inquiry
 * to a Contact, so an anonymous inquiry would reach nobody. Signing in
 * happens in the host page's `<mpp-user-login>` modal (or MP's hosted
 * sign-in with a round trip); the token change flips this form into its
 * ready state without a reload.
 */
export function InquiryForm({
  apiBaseUrl,
  group,
  finderUrl,
  inquiryFormUrl,
  tone = 'light',
  inquired = [],
  onSent,
  onHelp,
}: InquiryFormProps) {
  const t = TONES[tone];
  const [token, setToken] = useState<string | null>(() => getMpWidgetToken());
  const [members, setMembers] = useState<HouseholdMember[] | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [draft] = useState<Draft | null>(() => readDraft(group.id));
  const [selected, setSelected] = useState<number[]>(draft?.selected ?? []);
  const [others, setOthers] = useState<OtherPerson[]>(draft?.others ?? []);
  const [adding, setAdding] = useState(Boolean(draft?.pending));
  const [pending, setPending] = useState<OtherPerson>(draft?.pending ?? EMPTY_OTHER);
  const [comments, setComments] = useState(draft?.comments ?? '');
  const [pendingSend, setPendingSend] = useState(Boolean(draft?.pendingSend));
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ duplicate: boolean; names: string[] } | null>(null);

  const pendingRef = useRef(pendingSend);
  pendingRef.current = pendingSend;

  useEffect(() => onMpTokenChange(setToken), []);

  useEffect(() => {
    if (!token) {
      setMembers(null);
      return;
    }
    const controller = new AbortController();
    setMembersLoading(true);
    fetchHousehold(apiBaseUrl, controller.signal)
      .then((m) => {
        if (controller.signal.aborted) return;
        setMembers(m);
        // Nobody is picked for them. The one exception: they pressed "Sign in
        // and send" while signed out, where the form said it would go as
        // "you", so on the way back "you" is what gets selected.
        if (pendingRef.current) {
          const self = m?.find((x) => x.isSelf)?.contactId;
          if (self !== undefined) setSelected((cur) => (cur.length ? cur : [self]));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setMembersLoading(false);
      });
    return () => controller.abort();
  }, [apiBaseUrl, token]);

  const selfMember = members?.find((m) => m.isSelf) ?? null;
  // Already inquired (and not closed): shown with a check, not selectable.
  const inquiredIds = useMemo(
    () =>
      new Set(inquired.filter((i) => !i.isOther && i.status !== 'closed').map((i) => i.contactId)),
    [inquired]
  );
  const chosenMembers = useMemo(
    () =>
      (members ?? []).filter(
        (m) => selected.includes(m.contactId) && !inquiredIds.has(m.contactId)
      ),
    [members, selected, inquiredIds]
  );
  // The "someone else" fields can be complete but not yet added: Send takes
  // them along rather than making people find the Add button first.
  const pendingReady = adding && otherValid(pending);
  const extras = useMemo(
    () => [...others, ...(pendingReady ? [trimOther(pending)] : [])],
    [others, pending, pendingReady]
  );
  const people = useMemo<InquiryPerson[]>(() => {
    const extra = extras.map((o) => ({ other: o }));
    if (!token) return [{}, ...extra]; // {} = the signed-in person, once they are
    return [
      ...selected.filter((id) => !inquiredIds.has(id)).map((contactId) => ({ contactId })),
      ...extra,
    ];
  }, [token, selected, extras, inquiredIds]);
  const names = useMemo(
    () => [
      ...(token ? chosenMembers.map((m) => m.name) : ['You']),
      ...extras.map((o) => `${o.firstName} ${o.lastName}`),
    ],
    [token, chosenMembers, extras]
  );
  const count = people.length;
  const canSend = count > 0 && count <= MAX_PEOPLE && (token ? members !== null : true);

  const toggleMember = (contactId: number) =>
    setSelected((cur) =>
      cur.includes(contactId) ? cur.filter((id) => id !== contactId) : [...cur, contactId]
    );
  const addPending = () => {
    if (!otherValid(pending)) return;
    setOthers((cur) => [...cur, trimOther(pending)]);
    setPending(EMPTY_OTHER);
    setAdding(false);
  };
  const removeOther = (index: number) => setOthers((cur) => cur.filter((_, i) => i !== index));

  // Hooks stay above the early returns below (full group, sent), or React
  // sees a different hook count once the form flips to its sent state.
  const send = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await submitInquiry(apiBaseUrl, group.id, {
        people,
        comments: comments.trim(),
        website,
      });
      clearDraft(group.id);
      setPendingSend(false);
      const sentNames = result.results?.length ? result.results.map((r) => r.name) : names;
      setSent({ duplicate: Boolean(result.duplicate), names: sentNames });
      onSent?.(sentNames);
    } catch (err) {
      setPendingSend(false);
      // The sign-in ran out and MP would not renew it: sign in and send on return.
      if (err instanceof InquiryError && err.status === 401) {
        setToken(null);
        signInToSend();
      } else {
        setError(err instanceof Error ? err.message : 'Could not send your inquiry');
      }
    } finally {
      setSending(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    if (!token) {
      signInToSend();
      return;
    }
    void send();
  };

  // Sign in on the way: keep what they typed, send it when they're back.
  function signInToSend() {
    writeDraft(group.id, {
      comments,
      selected,
      others,
      pending: adding ? pending : null,
      pendingSend: true,
      at: Date.now(),
    });
    setPendingSend(true);
    signInAndReturn();
  }

  // They pressed Send before signing in. The token is here now (modal, or
  // the round trip through MP's sign-in) and their household has loaded:
  // finish what they started.
  useEffect(() => {
    if (!pendingSend || !token || sending || sent) return;
    if (members === null || people.length === 0) return; // household still loading
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSend, token, members, people.length]);

  if (group.full) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className={`text-base font-bold ${t.strong}`}>This group is full right now.</p>
        <p className={`text-sm ${t.muted}`}>
          Groups open up as the seasons change, but there are plenty of others with room. We can
          also help you find one that fits.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <a href={finderUrl} className={`${CTA} ${t.primary}`}>
            Find another group
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
          {onHelp ? (
            <button type="button" onClick={onHelp} className={`${CTA} ${t.secondary}`}>
              Help me find a group
            </button>
          ) : (
            <a href={inquiryFormUrl} className={`${CTA} ${t.secondary}`}>
              Help me find a group
            </a>
          )}
        </div>
      </div>
    );
  }

  if (sent) {
    const onlySelf =
      sent.names.length === 1 && selfMember !== null && sent.names[0] === selfMember.name;
    const plural = sent.names.length > 1;
    return (
      <div className="flex flex-col items-start gap-3">
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${t.check}`}
        >
          <Check className="h-6 w-6" strokeWidth={3} />
        </span>
        <p className={`text-base font-bold ${t.strong}`}>
          {sent.duplicate
            ? `The leaders already have your ${plural ? 'inquiries' : 'inquiry'}.`
            : plural
              ? 'Your inquiries are on their way.'
              : 'Your inquiry is on its way.'}
        </p>
        <p className={`text-sm ${t.muted}`}>
          {onlySelf
            ? 'We told the leaders you are interested, and one of them will reach out soon.'
            : `We told the leaders that ${listNames(sent.names)} ${plural ? 'are' : 'is'} interested, and one of them will be in touch soon.`}
          {selfMember?.email ? ` A copy is in your inbox at ${selfMember.email}.` : ''}
        </p>
        <a href={finderUrl} className={`text-sm font-semibold hover:underline ${t.link}`}>
          Back to all groups
        </a>
      </div>
    );
  }

  const reachNote = (() => {
    if (!token || chosenMembers.length !== 1 || extras.length > 0) return null;
    const m = chosenMembers[0];
    const at = [m.email, m.phone].filter(Boolean).join(' or ');
    return at ? `The leaders will reach ${m.isSelf ? 'you' : m.name} at ${at}.` : null;
  })();

  return (
    <form onSubmit={submit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <span className={`text-xs font-bold tracking-widest uppercase ${t.label}`}>
          Who's interested?
        </span>
        {!token ? (
          <p className={`text-sm ${t.muted}`}>
            You. You'll sign in on the way so the leaders know who to reach, and we'll send this the
            moment you're back. Bringing someone who isn't on our site? Add them too.
          </p>
        ) : membersLoading && !members ? (
          <div className={`h-12 w-full max-w-sm animate-pulse rounded-full ${t.skeleton}`} />
        ) : members && members.length > 0 ? null : (
          <p className={`text-sm ${t.faint}`}>
            We couldn't load your profile. Try signing in again.
          </p>
        )}
        {(!token || members) && (
          <div className="flex flex-wrap gap-2">
            {(members ?? []).map((m) => {
              const done = inquiredIds.has(m.contactId);
              const active = !done && selected.includes(m.contactId);
              return (
                <button
                  key={m.contactId}
                  type="button"
                  onClick={() => !done && toggleMember(m.contactId)}
                  aria-pressed={active}
                  disabled={done}
                  title={done ? 'Already inquired' : undefined}
                  className={`${CHIP} ${active ? t.selected : done ? `${CHIP_IDLE} cursor-default opacity-60 hover:scale-100 hover:shadow-none` : CHIP_IDLE}`}
                >
                  <span className="relative">
                    <Avatar name={m.name} imageUrl={m.imageUrl} size={28} />
                    {active && (
                      <span
                        className={`absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded-full ring-2 ${t.tick}`}
                      >
                        <Check className="h-2.5 w-2.5" strokeWidth={4} />
                      </span>
                    )}
                  </span>
                  {m.name}
                  {done ? (
                    <span className="text-xs font-normal opacity-70">inquired</span>
                  ) : (
                    m.isSelf && <span className="text-xs font-normal opacity-70">(you)</span>
                  )}
                </button>
              );
            })}
            {others.map((o, i) => (
              <span key={`other-${i}`} className={`${CHIP} ${t.selected} pr-1.5`}>
                <Avatar name={`${o.firstName} ${o.lastName}`} imageUrl={null} size={28} />
                {o.firstName} {o.lastName}
                <button
                  type="button"
                  onClick={() => removeOther(i)}
                  aria-label={`Remove ${o.firstName}`}
                  className="ml-1 flex h-6 w-6 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={3} />
                </button>
              </span>
            ))}
            {!adding && count < MAX_PEOPLE && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className={`${CHIP} ${CHIP_DASHED}`}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                  <UserPlus className="h-4 w-4" strokeWidth={2.5} />
                </span>
                Someone else
              </button>
            )}
          </div>
        )}
        {adding && (
          <div className="mt-1 grid gap-2 sm:grid-cols-2 @3xl:grid-cols-4">
            <input
              value={pending.firstName}
              onChange={(e) => setPending({ ...pending, firstName: e.target.value })}
              placeholder="First name"
              autoComplete="off"
              className={FIELD}
            />
            <input
              value={pending.lastName}
              onChange={(e) => setPending({ ...pending, lastName: e.target.value })}
              placeholder="Last name"
              autoComplete="off"
              className={FIELD}
            />
            <input
              type="email"
              value={pending.email}
              onChange={(e) => setPending({ ...pending, email: e.target.value })}
              placeholder="Their email"
              autoComplete="off"
              className={FIELD}
            />
            <input
              type="tel"
              value={pending.phone}
              onChange={(e) => setPending({ ...pending, phone: e.target.value })}
              placeholder="Their phone"
              autoComplete="off"
              className={FIELD}
            />
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2 @3xl:col-span-4">
              <button
                type="button"
                onClick={addPending}
                disabled={!otherValid(pending)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 ${t.secondary}`}
              >
                <Plus className="h-4 w-4" strokeWidth={3} />
                Add {pending.firstName.trim() || 'them'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPending(EMPTY_OTHER);
                  setAdding(false);
                }}
                className={`rounded-full px-3 py-2 text-sm font-semibold transition-colors ${t.ghost}`}
              >
                Cancel
              </button>
              <p className={`text-xs ${t.faint}`}>
                An email or a phone number, so the leaders can reach them.
              </p>
            </div>
          </div>
        )}
        {reachNote && <p className={`text-xs ${t.faint}`}>{reachNote}</p>}
      </div>

      <label className="flex flex-col gap-2">
        <span className={`text-xs font-bold tracking-widest uppercase ${t.label}`}>
          Tell the leaders a little about {count > 1 ? 'yourselves' : 'yourself'}
        </span>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Who you are, what you're hoping for in a group, anything they should know."
          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[#299c8f] focus:ring-2 focus:ring-[#299c8f]/30 focus:outline-none"
        />
      </label>

      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      {error && <p className="text-sm font-semibold text-red-600">{error}</p>}

      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={!canSend || sending} className={`${CTA} ${t.primary}`}>
          {sending
            ? 'Sending…'
            : !token
              ? 'Sign in and send'
              : count > 1
                ? `Send ${count} inquiries`
                : 'Send inquiry'}
          {!sending && !token && <LogIn className="h-4 w-4" strokeWidth={2.5} />}
          {!sending && token && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
        </button>
        {token && count === 0 && (
          <span className={`text-xs ${t.faint}`}>Pick at least one person.</span>
        )}
        {group.paused && (
          <span className={`text-xs ${t.faint}`}>
            This group is on a break; the leaders will reach out about when it resumes.
          </span>
        )}
      </div>
    </form>
  );
}
